// Modal for creating a new action and optionally assigning it to a staff
// member (G2). Mirrors ReassignActionDialog. On submit calls mcp.addActionItem
// (source defaults to "manual" server-side); the parent refreshes via onCreated.

import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/lib/supabase";
import { mcp, type Priority } from "@/lib/mcp";
import { notifyActionAssignment } from "@/lib/actionsNotify";

interface StaffOption {
  id: string;
  display_name: string;
  role_label: string | null;
}

const PRIORITIES: Priority[] = ["low", "normal", "high", "urgent", "critical"];

export function NewActionDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New action</DialogTitle>
          <DialogDescription>
            Create an action and optionally assign it to a staff member.
          </DialogDescription>
        </DialogHeader>

        {/* Mounted only while open, so the form starts fresh each time
            instead of being reset from an effect. */}
        {open && <NewActionDialogBody onOpenChange={onOpenChange} onCreated={onCreated} />}
      </DialogContent>
    </Dialog>
  );
}

function NewActionDialogBody({
  onOpenChange,
  onCreated,
}: {
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [priority, setPriority] = useState<Priority>("normal");
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Load active, non-system staff for the assignee picker (mirrors
  // ReassignActionDialog).
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .schema("staff")
        .from("users")
        .select("id, display_name, role_label, metadata, is_active")
        .eq("is_active", true)
        .order("display_name", { ascending: true });
      if (cancelled) return;
      if (error) {
        toast.error(`Couldn't load staff: ${error.message}`);
        setLoadingStaff(false);
        return;
      }
      const filtered: StaffOption[] = (data ?? [])
        .filter((row) => {
          const meta = (row.metadata as Record<string, unknown> | null) ?? null;
          return !meta?.is_system_user;
        })
        .map((row) => ({
          id: row.id as string,
          display_name: row.display_name as string,
          role_label: (row.role_label as string | null) ?? null,
        }));
      setStaff(filtered);
      setLoadingStaff(false);
    })();

    return () => { cancelled = true; };
  }, []);

  const handleCreate = async () => {
    const t = title.trim();
    if (!t) {
      toast.error("Give the action a title.");
      return;
    }
    setSubmitting(true);
    try {
      const { action } = await mcp.addActionItem({
        title: t,
        description: description.trim() || undefined,
        owner_user_id: ownerId || undefined,
        priority,
        due_date: dueDate || undefined,
      });
      toast.success("Action created.");
      onCreated();
      onOpenChange(false);
      // Best-effort, non-blocking — notify whoever ended up owning the
      // action, including when the server-side default-owner fallback (#472)
      // assigned one even though this form was left on "Unassigned".
      const code = (action as { code?: string } | null)?.code;
      const owner = (action as { owner_user_id?: string | null } | null)?.owner_user_id;
      if (code && owner) void notifyActionAssignment(code, "created");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="new-action-title" className="text-xs">Title</Label>
          <Input
            id="new-action-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs doing?"
            disabled={submitting}
            autoFocus
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-action-owner" className="text-xs">Assign to</Label>
            <Select
              id="new-action-owner"
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              disabled={loadingStaff || submitting}
            >
              <option value="">{loadingStaff ? "Loading…" : "Unassigned"}</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.display_name}{s.role_label ? ` — ${s.role_label}` : ""}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-action-priority" className="text-xs">Priority</Label>
            <Select
              id="new-action-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as Priority)}
              disabled={submitting}
              className="capitalize"
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="new-action-due" className="text-xs">Due date</Label>
          <Input
            id="new-action-due"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            disabled={submitting}
          />
          <p className="text-[10px] text-muted-foreground">Leave blank to default to 48 hours.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="new-action-desc" className="text-xs">Description (optional)</Label>
          <Textarea
            id="new-action-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Any detail or context."
            rows={3}
            disabled={submitting}
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={() => { void handleCreate(); }} disabled={submitting || !title.trim()}>
          {submitting ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Creating…</>
          ) : (
            <><Plus className="h-3.5 w-3.5 mr-1" /> Create action</>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
